import PageSEO from '../components/PageSEO';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { PremiumBlur, PremiumLock, PremiumCTA } from '../components/PremiumGate';
import { usePremium } from '../hooks/usePremium';
import { trackAIView } from '../utils/analytics';
import {
  ArrowLeft, Send, Loader, Stethoscope, ClipboardList,
  Target, Upload, Calendar, ChevronRight, Sparkles, Crown
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DOCTOR_IMG = 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=120&h=120&fit=crop&crop=face';

const safeText = (val) => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(v => safeText(v)).join(', ');
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
};

/* ── Assessment Paragraphs — renders the main doctor text with proper spacing ── */
const DoctorProse = ({ text }) => {
  if (!text) return null;
  const safe = safeText(text);
  // Handle both \n\n and <br> variants for paragraph separation
  const normalized = safe.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '\n\n').replace(/<br\s*\/?>/gi, '\n');
  const paragraphs = normalized.split(/\n\n+/).filter(p => p.trim());
  return (
    <div className="space-y-6" data-testid="doctor-prose">
      {paragraphs.map((p, i) => {
        const lines = p.split('\n').filter(l => l.trim());
        return (
          <div key={i}>
            {lines.map((line, j) => (
              <p key={j} style={{
                color: '#D6DCE8',
                fontSize: 'clamp(17px, 1.15vw, 20px)',
                lineHeight: 1.78,
                letterSpacing: '-0.01em',
                marginBottom: j < lines.length - 1 ? '8px' : 0,
              }}>{line}</p>
            ))}
          </div>
        );
      })}
    </div>
  );
};

/* ── Premium Blur Assessment — free kullanıcıya ilk 2 paragraf gösterir ── */
const PremiumBlurAssessment = ({ isPremium, hasAssessment, assessmentText, aiLoading, forceRegenerate }) => {
  const navigate = useNavigate();
  const cardStyle = {
    background: 'linear-gradient(170deg, rgba(14,22,45,0.55), rgba(10,16,34,0.65))',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderRadius: '24px', border: '1px solid rgba(255,255,255,0.06)',
    boxShadow: '0 0 40px rgba(34,211,238,0.04), 0 20px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)',
    padding: 'clamp(28px, 4vw, 44px) clamp(24px, 3.5vw, 40px)',
  };

  if (!hasAssessment && !aiLoading) {
    return (
      <div className="relative mb-10" style={cardStyle} data-testid="assessment-card">
        <div className="text-center py-12">
          <p style={{ color: '#475569', fontSize: '16px', marginBottom: '16px' }}>Değerlendirme henüz oluşturulmadı.</p>
          <button onClick={forceRegenerate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.03]"
            style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)', color: '#67E8F9' }} data-testid="generate-assessment-btn">
            <Sparkles className="w-4 h-4" /> Değerlendirme Oluştur
          </button>
        </div>
      </div>
    );
  }

  if (aiLoading) {
    return (
      <div className="relative mb-10" style={cardStyle} data-testid="assessment-card">
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-14 h-14 rounded-full" style={{ border: '2px solid rgba(34,211,238,0.2)', borderTopColor: '#22D3EE', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#64748B', fontSize: '15px' }}>Doktorun verilerini inceliyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-10" style={cardStyle} data-testid="assessment-card">
      <div className="absolute top-0 left-8 right-8 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.15), transparent)' }} />
      {isPremium ? (
        <>
          <DoctorProse text={assessmentText} />
          <div className="flex justify-end mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <button onClick={forceRegenerate} disabled={aiLoading} className="flex items-center gap-2 text-xs transition-all hover:text-cyan-300 disabled:opacity-30" style={{ color: '#475569' }} data-testid="refresh-assessment-btn">
              <Sparkles className="w-3.5 h-3.5" /> Yeniden Değerlendir
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Blur preview */}
          <div className="relative">
            <div style={{ maxHeight: '9em', overflow: 'hidden', lineHeight: '1.8em' }}>
              <DoctorProse text={assessmentText} />
            </div>
            <div className="absolute inset-0" style={{
              top: '5em',
              background: 'linear-gradient(180deg, transparent 0%, rgba(10,18,38,0.7) 30%, rgba(10,18,38,0.98) 100%)',
              backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            }} />
          </div>
          {/* Conversion CTA */}
          <div className="relative mt-2 rounded-2xl p-6 text-center" style={{
            background: 'linear-gradient(170deg, rgba(250,204,21,0.04), rgba(245,158,11,0.02))',
            border: '1px solid rgba(250,204,21,0.1)',
          }} data-testid="premium-doctor-cta">
            <p className="text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Bu analiz sizin verilerinize özel olarak devam ediyor
            </p>
            <p className="text-xs mb-5" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Kişisel risk analizi, öneriler ve takip planınızı görün
            </p>
            <button onClick={() => navigate('/premium')}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-bold text-black transition-all hover:scale-105 hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #FACC15, #F59E0B)',
                boxShadow: '0 8px 30px rgba(250,204,21,0.35), 0 0 20px rgba(250,204,21,0.15)',
              }}
              data-testid="premium-doctor-unlock-btn">
              <Crown className="w-4 h-4" /> Detaylı Analizi Gör
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default function AIDoctorPage() {
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [testCompletedName, setTestCompletedName] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => {
    const testCompleted = searchParams.get('testCompleted');
    const testName = searchParams.get('testName');
    if (testCompleted === 'true') {
      setTestCompletedName(testName || 'Değerlendirme');
      // Clean up URL params
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    trackAIView();
    const load = async () => {
      try {
        const [detailRes, historyRes] = await Promise.all([
          axios.get(`${BACKEND}/api/user/doctor-detail`, { withCredentials: true }),
          axios.get(`${BACKEND}/api/user/doctor-chat/history`, { withCredentials: true }),
        ]);
        setDetail(detailRes.data);
        if (historyRes.data?.length) {
          setChatMessages(historyRes.data.map(c => ([
            { role: 'user', text: c.question },
            { role: 'doctor', text: c.answer },
          ])).flat());
        }
        setLoading(false);

        // If coming from a completed test OR no cached analysis, regenerate
        const cached = detailRes.data?.ai_sections;
        const hasCached = cached && (cached.assessment || cached.genel_degerlendirme);
        const fromTest = searchParams.get('testCompleted') === 'true';
        if (!hasCached || fromTest) {
          setAiLoading(true);
          try {
            const aiRes = await axios.post(`${BACKEND}/api/user/doctor-detail/generate`, {}, { withCredentials: true, timeout: 120000 });
            if (aiRes.data?.ai_sections) {
              setDetail(prev => ({ ...prev, ai_sections: aiRes.data.ai_sections }));
            }
          } catch (e2) { console.error('AI generation failed:', e2); }
          finally { setAiLoading(false); }
        }
      } catch (e) {
        if (e.response?.status === 401) navigate('/login');
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  useEffect(() => { scrollToBottom(); }, [chatMessages, scrollToBottom]);

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/user/doctor-chat`, { message: msg }, { withCredentials: true });
      setChatMessages(prev => [...prev, { role: 'doctor', text: data.answer }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'doctor', text: 'Bir hata oluştu, lütfen tekrar deneyin.' }]);
    } finally { setChatLoading(false); }
  };

  const forceRegenerate = async () => {
    setAiLoading(true);
    try {
      // Clear cached evaluation to force fresh generation
      const aiRes = await axios.post(`${BACKEND}/api/user/doctor-detail/generate`, {}, { withCredentials: true, timeout: 120000 });
      if (aiRes.data?.ai_sections) {
        setDetail(prev => ({ ...prev, ai_sections: aiRes.data.ai_sections }));
      }
    } catch (e2) { console.error('Regeneration failed:', e2); }
    finally { setAiLoading(false); }
  };

  /* ── LOADING ── */
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'transparent' }}>
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[70vh] gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden border-2" style={{ borderColor: 'rgba(34,211,238,0.3)' }}>
              <img src={DOCTOR_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: '#0A0F1C', border: '2px solid rgba(34,211,238,0.4)' }}>
              <Loader className="w-4 h-4 text-cyan-400 animate-spin" />
            </div>
          </div>
          <p style={{ color: '#64748B', fontSize: '16px' }}>Doktorun seni analiz ediyor...</p>
        </div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div style={{ minHeight: '100vh', background: 'transparent' }}>
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[70vh] text-white/60 gap-4">
          <p style={{ fontSize: '16px' }}>Henüz yeterli veri yok.</p>
          <button onClick={() => navigate('/dashboard')} className="text-cyan-400 hover:text-cyan-300 flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard'a Dön
          </button>
        </div>
      </div>
    );
  }

  const ai = detail.ai_sections || {};
  // Support both new format (assessment) and old format (genel_degerlendirme)
  const assessmentText = ai.assessment || ai.genel_degerlendirme || '';
  const summaryText = ai.summary || '';
  const actionItems = Array.isArray(ai.actions) ? ai.actions : [];
  const hasAssessment = !!assessmentText;

  return (
    <div style={{
      fontFamily: '"Inter", "SF Pro Display", "Manrope", system-ui, sans-serif',
      minHeight: '100vh',
      background: 'transparent',
    }}>
      <Header />
      <PageSEO title="AI Doktor Değerlendirmesi" description="AI destekli kişisel sağlık değerlendirmesi ve öneriler" />      <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-28">
        {/* ── Back ── */}
        <button onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 mb-10 text-white/35 hover:text-white/60 transition-colors"
          style={{ fontSize: '14px' }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Dashboard'a Dön
        </button>

        {/* ── Test Completion Congratulation Banner ── */}
        {testCompletedName && (
          <div className="mb-8 p-5 rounded-2xl relative overflow-hidden animate-fadeIn"
            style={{
              background: 'linear-gradient(135deg, rgba(52,211,153,0.12), rgba(34,211,238,0.08))',
              border: '1px solid rgba(52,211,153,0.25)',
            }}
            data-testid="test-completed-banner">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)' }}>
                <Sparkles className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p style={{ color: '#6EE7B7', fontSize: '16px', fontWeight: 700 }}>
                  Tebrikler! {testCompletedName} tamamlandı.
                </p>
                <p style={{ color: '#94A3B8', fontSize: '13px', marginTop: '4px' }}>
                  {aiLoading ? 'Doktorun yeni sonuçlarını değerlendiriyor...' : 'Doktorun değerlendirmesi güncellendi.'}
                </p>
              </div>
              <button onClick={() => setTestCompletedName('')}
                className="ml-auto text-white/30 hover:text-white/60 transition text-lg"
                data-testid="close-test-banner">&times;</button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════
            1. DOCTOR HEADER — Avatar + Summary
        ════════════════════════════════════════════ */}
        <div className="flex items-start gap-5 mb-10" data-testid="doctor-header">
          <div className="shrink-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden" style={{
              border: '2px solid rgba(34,211,238,0.25)',
              boxShadow: '0 0 28px rgba(34,211,238,0.12)',
            }}>
              <img src={DOCTOR_IMG} alt="AI Doktor" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <h1 className="font-bold text-white" style={{
              fontSize: 'clamp(22px, 3vw, 32px)', letterSpacing: '-0.03em', lineHeight: 1.15
            }} data-testid="page-title">
              Doktorunun Değerlendirmesi
            </h1>
            {summaryText ? (
              <p className="mt-2" style={{ color: '#94A3B8', fontSize: 'clamp(15px, 1.1vw, 18px)', lineHeight: 1.5 }} data-testid="doctor-summary">
                {safeText(summaryText)}
              </p>
            ) : (
              <p className="mt-2" style={{ color: '#475569', fontSize: '15px' }}>
                Kişisel verilerine göre oluşturulan değerlendirme
              </p>
            )}
            {/* Data source badges */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {detail.data_sources?.has_onboarding && <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(34,211,238,0.08)', color: '#67E8F9', border: '1px solid rgba(34,211,238,0.15)' }}>Onboarding</span>}
              {detail.data_sources?.test_count > 0 && <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(167,139,250,0.08)', color: '#C4B5FD', border: '1px solid rgba(167,139,250,0.15)' }}>{detail.data_sources.test_count} Test</span>}
              {detail.data_sources?.checkin_count > 0 && <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(52,211,153,0.08)', color: '#6EE7B7', border: '1px solid rgba(52,211,153,0.15)' }}>{detail.data_sources.checkin_count} Check-in</span>}
              {detail.data_sources?.document_count > 0 && <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(251,191,36,0.08)', color: '#FDE68A', border: '1px solid rgba(251,191,36,0.15)' }}>{detail.data_sources.document_count} Belge</span>}
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            2. MAIN DOCTOR ASSESSMENT CARD
        ════════════════════════════════════════════ */}
        <PremiumBlurAssessment isPremium={isPremium} hasAssessment={hasAssessment} assessmentText={assessmentText} aiLoading={aiLoading} forceRegenerate={forceRegenerate} />

        {/* ════════════════════════════════════════════
            3. ŞİMDİ NE YAPALIM — CTA SECTION
        ════════════════════════════════════════════ */}
        <div className="mb-10" data-testid="actions-section">
          <h2 className="font-bold text-white mb-5" style={{ fontSize: 'clamp(18px, 1.8vw, 22px)', letterSpacing: '-0.02em' }}>
            Şimdi Ne Yapalım?
          </h2>

          {/* Action items from AI */}
          {actionItems.length > 0 && (
            <div className="space-y-3 mb-6">
              {actionItems.map((action, i) => (
                <div key={i} className="flex items-start gap-3 px-5 py-3.5 rounded-2xl" style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{
                    background: 'rgba(34,211,238,0.1)',
                    color: '#67E8F9', fontSize: '12px', fontWeight: 800,
                  }}>
                    {i + 1}
                  </div>
                  <p style={{ color: '#C8D0E0', fontSize: '15px', lineHeight: 1.6 }}>{safeText(action)}</p>
                </div>
              ))}
            </div>
          )}

          {/* CTA Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {detail.recommended_test?.test_id && (
              <button onClick={() => navigate(detail.recommended_test.test_id === '__cardio__' ? '/cardio' : `/test/${detail.recommended_test.test_id}`)}
                className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all hover:scale-[1.03] group"
                style={{
                  background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.12)',
                  boxShadow: '0 0 20px rgba(34,211,238,0.06)',
                }}
                data-testid="cta-start-test">
                <ClipboardList className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                <span style={{ color: '#67E8F9', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Teste Başla</span>
              </button>
            )}
            <button onClick={() => navigate('/dashboard')}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all hover:scale-[1.03] group"
              style={{
                background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.12)',
                boxShadow: '0 0 20px rgba(52,211,153,0.06)',
              }}
              data-testid="cta-daily-task">
              <Target className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
              <span style={{ color: '#6EE7B7', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Günlük Görev</span>
            </button>
            <button onClick={() => navigate('/documents')}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all hover:scale-[1.03] group"
              style={{
                background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.12)',
                boxShadow: '0 0 20px rgba(251,191,36,0.06)',
              }}
              data-testid="cta-upload-doc">
              <Upload className="w-5 h-5 text-amber-400 group-hover:text-amber-300 transition-colors" />
              <span style={{ color: '#FDE68A', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Belge Yükle</span>
            </button>
            <button onClick={() => navigate('/dashboard')}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl transition-all hover:scale-[1.03] group"
              style={{
                background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.12)',
                boxShadow: '0 0 20px rgba(167,139,250,0.06)',
              }}
              data-testid="cta-checkin">
              <Calendar className="w-5 h-5 text-violet-400 group-hover:text-violet-300 transition-colors" />
              <span style={{ color: '#C4B5FD', fontSize: '13px', fontWeight: 600, textAlign: 'center' }}>Check-in Yap</span>
            </button>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            4. DOKTORUNA SOR — CHAT
        ════════════════════════════════════════════ */}
        <div className="relative overflow-hidden" style={{
          background: 'linear-gradient(170deg, rgba(14,22,45,0.45), rgba(10,16,34,0.55))',
          backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
          borderRadius: '24px',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.03)',
        }} data-testid="section-doktora-sor">
          {/* Header */}
          <div style={{ padding: '28px 28px 0 28px' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.18)',
              }}>
                <Stethoscope className="w-5 h-5" style={{ color: '#22D3EE' }} />
              </div>
              <h2 className="font-bold text-white" style={{ fontSize: 'clamp(18px, 1.8vw, 22px)', letterSpacing: '-0.02em' }}>
                Doktoruna Sor
              </h2>
            </div>
            <p className="mb-4" style={{ color: '#64748B', fontSize: '14px', lineHeight: 1.5 }}>
              Aklına takılan şeyi yaz. Doktorun, kendi verilerine göre sana cevap versin.
            </p>
          </div>

          {/* Chat messages */}
          <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0 28px' }}>
            {chatMessages.length === 0 && (
              <div className="py-10 space-y-3">
                <p className="text-center" style={{ color: '#334155', fontSize: '14px' }}>
                  Bir soru sorarak başlayabilirsin.
                </p>
                {/* Quick suggestions */}
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    'Son zamanlarda neden bu kadar yorgunum?',
                    'Uyku düzenim neden bozuk?',
                    'Hafızam neden zayıfladı?',
                  ].map((q, i) => (
                    <button key={i} onClick={() => { setChatInput(q); }}
                      className="px-3 py-1.5 rounded-full text-xs transition-all hover:scale-[1.03]"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: '#64748B' }}
                      data-testid={`suggestion-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex mb-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'doctor' && (
                  <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 mr-3 mt-1" style={{ border: '1px solid rgba(34,211,238,0.2)' }}>
                    <img src={DOCTOR_IMG} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${msg.role === 'user' ? 'rounded-br-lg' : 'rounded-bl-lg'}`} style={{
                  background: msg.role === 'user' ? 'rgba(34,211,238,0.08)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${msg.role === 'user' ? 'rgba(34,211,238,0.14)' : 'rgba(255,255,255,0.05)'}`,
                }}>
                  <p style={{ color: '#C8D0E0', fontSize: '15px', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{safeText(msg.text)}</p>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start mb-4">
                <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 mr-3 mt-1" style={{ border: '1px solid rgba(34,211,238,0.2)' }}>
                  <img src={DOCTOR_IMG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="px-4 py-3 rounded-2xl rounded-bl-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-cyan-400/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-cyan-400/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-cyan-400/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          {/* Chat input — Premium gate */}
          {isPremium ? (
          <div style={{ padding: '16px 28px 28px 28px' }}>
            <div className="flex gap-3">
              <textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Doktoruna bir soru sor..."
                rows={2}
                className="flex-1 rounded-2xl outline-none resize-none"
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                  color: '#C8D0E0', padding: '14px 16px', fontSize: '15px', lineHeight: 1.5,
                }}
                data-testid="doctor-chat-input"
              />
              <button
                onClick={sendChat}
                disabled={chatLoading || !chatInput.trim()}
                className="self-end rounded-xl flex items-center justify-center transition-all hover:scale-105 disabled:opacity-25"
                style={{
                  width: '48px', height: '48px',
                  background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)',
                }}
                data-testid="doctor-chat-send"
              >
                <Send className="w-5 h-5" style={{ color: '#67E8F9' }} />
              </button>
            </div>
          </div>
          ) : (
          <div style={{ padding: '16px 28px 28px 28px' }}>
            <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(250,204,21,0.04)', border: '1px solid rgba(250,204,21,0.1)' }}>
              <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Doktorunuza soru sormak için Premium erişim gerekir</p>
              <button onClick={() => navigate('/premium')}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-black transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #FACC15, #F59E0B)', boxShadow: '0 4px 15px rgba(250,204,21,0.3)' }}
                data-testid="premium-chat-unlock-btn">
                <Crown className="w-4 h-4" /> Detaylı Analizi Gör
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-center mt-4" style={{ color: 'rgba(255,255,255,0.08)', fontSize: '11px' }}>
          Bu değerlendirme tanı yerine geçmez. Ciddi belirtilerde bir sağlık profesyoneline danışın.
        </p>
      </div>

      <div className="relative z-10"><Footer /></div>

      {/* Animations */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
      `}</style>
    </div>
  );
}
